import React from "react";
import { Box, styled } from "@mui/material";
import Muitabs from "@mui/material/Tabs";
import MuiTab from "@mui/material/Tab";
import portalSettingsManager from "utils/portalSettingsManager";
import { TabModel } from "utils/types";

import { useTranslation } from "react-i18next";
import LoaderWrapper from "pages/profile/LoaderWrapper";

const CircleIcon = styled(Box)(({ theme }) => ({
  backgroundColor:
    portalSettingsManager.application?.common?.themeColor || theme.palette.primary.main,
  borderRadius: "50%",
  width: "9px",
  height: "9px",
  position: "absolute",
  right: 6,
  top: 25,
}));

const Tab = styled(MuiTab, {
  shouldForwardProp: (prop) => prop !== "mobile",
})<{ mobile?: boolean }>(({ theme, mobile }) => ({
  minWidth: 75,
  paddingLeft: 17,
  paddingRight: 17,
  fontSize: mobile ? "12px" : undefined,
  "&:focus": { border: "2px solid #000000" },
  fontWeight: 700,
  color: theme.palette.grey["800"] + "!important",
  textTransform: "none",
  [theme.breakpoints.down("md")]: {
    flex: 1,
  },
}));

const MTabs = styled(Muitabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.grey["300"]}`,
  "& button": {
    [theme.breakpoints.down("sm")]: {
      padding: 0,
      fontSize: 13,
    },
  },
  "& .MuiTabs-indicator": {
    background: portalSettingsManager.application?.common?.themeColor || theme.palette.primary.main,
    height: "3px",
  },
}));

interface TabPanelProps {
  index: string | number;
  value: string | number;
  style?: React.CSSProperties;
  persistInDom?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}
export const TabPanel: React.FC<TabPanelProps> = (props) => {
  const { children, value, index, persistInDom = false, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index || persistInDom ? (
        <Box p={3} {...other}>
          {props.loading && <LoaderWrapper loading />}
          {
            <Box visibility={props.loading ? "hidden" : "visible"} component="div">
              {children}
            </Box>
          }
        </Box>
      ) : (
        ""
      )}
    </div>
  );
};

type TabsProps<T> = {
  value?: T | 0;
  mobile?: boolean;
  tabs: TabModel<T>[];
  onChange: (event: React.ChangeEvent<unknown>, value: T | 0) => void;
  height?: number;
};

export const Tabs = <T,>({
  value = 0,
  tabs,
  onChange,
  mobile,
  height,
}: TabsProps<T>): React.ReactElement => {
  const { t } = useTranslation();
  const handleChange = (event: React.ChangeEvent<unknown>, value: T | 0) => {
    onChange(event, value);
  };

  return (
    <MTabs value={value} onChange={handleChange} aria-label={t("aria_label_profile_tabs")}>
      {tabs.map((item, index) => {
        return (
          <Tab
            style={{ minHeight: height }}
            mobile={mobile}
            key={index}
            label={item.label}
            icon={item.isDirty ? <CircleIcon /> : <></>}
            value={item.value || index}
          />
        );
      })}
    </MTabs>
  );
};
